<template>
  <div class="product-attributes">
    <div class="attributes-header">
      <h3>{{ header }}</h3>
      <button class="add-attribute-btn" @click="showAddAttributeModal = true">
        Add Attribute
      </button>
    </div>

    <table>
      <thead>
        <tr>
          <th>Attribute</th>
          <th>Value</th>
          <th v-if="isVariantAttributes">Sources</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <v-tooltip
        location="left"
        v-for="(dataObj, idx) in filteredAttributes">
          <template v-slot:activator="{ props }">
            <tr v-if="shouldHaveGroupHeader(dataObj, idx)" style="color: black; border-bottom: 2px solid #aaa;">
              <td :colspan="isVariantAttributes ? 4 : 3">
                <strong>{{ getGroup(dataObj) || "No Group" }}</strong>
              </td>
            </tr>
            <tr
              v-bind="props"
              :key="idx"
              :class="[
                { flagged: isFlagged(dataObj.displayName) },
                { highlighted: hoveredRow === dataObj.rawAttrName },
              ]"
          @mouseenter="hoverRow(dataObj.displayName)"
          @mouseleave="leaveRow"
            >
              <td class="attribute-key">
                <span>
                  {{ isSourceAttributes ? dataObj.displayName : dataObj.rawAttrName }}
                </span>
              </td>

              <td class="attribute-value">
                <template v-if="getValueOptions(dataObj.originalKey).length">
                  <div class="custom-dropdown-wrapper">
                    <div
                      v-if="editingAttributeKey !== dataObj.originalKey"
                      class="display-value"
                      @click="startEditing(dataObj, $event)"
                      title="Click to edit"
                    >
                      <template v-if="dataObj.multipleValues.length > 1">
                        <span
                          v-for="(val, i) in dataObj.multipleValues"
                          :key="i"
                          class="multi-value-tag"
                        >
                          {{ val }}
                        </span>
                      </template>
                      <template v-else>
                        {{ dataObj.displayValue || "—" }}
                      </template>
                    </div>
                  </div>

                  <Teleport to="body">
                    <div
                      v-if="editingAttributeKey === dataObj.originalKey"
                      ref="teleportedDropdown"
                      class="searchable-dropdown"
                      :style="dropdownStyles"
                    >
                      <input
                        ref="dropdownInput"
                        type="text"
                        class="searchable-dropdown-input"
                        v-model="searchQuery"
                        placeholder="Search..."
                        @keydown.esc="cancelEditing"
                      />
                      <div class="dropdown-options">
                        <div v-if="!filteredDropdownOptions.length" class="dropdown-option-none">
                          No matches found
                        </div>
                        <template v-if="!isMultiSelect(dataObj)">
                          <div
                            v-for="opt in filteredDropdownOptions"
                            :key="opt.code"
                            class="dropdown-option"
                            @click="selectOption(dataObj, opt.code)"
                          >
                            {{ opt.label }}
                          </div>
                        </template>
                        <template v-else>
                          <div
                            v-for="opt in filteredDropdownOptions"
                            :key="opt.code"
                            class="dropdown-option multi"
                            @click.stop="toggleMultiSelectOption(dataObj, opt.code)"
                          >
                            <input
                              type="checkbox"
                              :checked="dataObj.valueCodes.includes(opt.code)"
                              class="multi-select-checkbox"
                              readonly
                            />
                            <label>{{ opt.label }}</label>
                          </div>
                        </template>
                      </div>
                    </div>
                  </Teleport>
                </template>

                <template v-else-if="dataObj.multipleValues && dataObj.multipleValues.length > 1">
                  <div class="multi-attr-values">
                    <div
                      v-for="(val, i) in dataObj.multipleValues"
                      :key="i"
                      class="multi-value-item"
                    >
                      <span
                        contenteditable
                        @input="(e) => onMultiAttributeValueEdit(dataObj.originalKey, dataObj.multipleValues, i, e)"
                      >
                        {{ val }}
                      </span>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <span
                    contenteditable
                    @input="onAttributeValueEdit(dataObj.originalKey, $event)"
                  >
                    {{ dataObj.displayValue }}
                  </span>
                </template>
              </td>

              <td v-if="isVariantAttributes" class="attribute-sources">
                <div class="sources-list">
                  <span
                    v-for="source in getAttributeSources(dataObj.rawAttrName)"
                    :key="source"
                    class="source-badge"
                    :style="{ backgroundColor: getSourceColor(source) }"
                  >
                    {{ source }}
                  </span>
                  <span v-if="!getAttributeSources(dataObj.rawAttrName).length" class="no-source">
                    —
                  </span>
                </div>
              </td>

              <td class="attribute-actions">
                <button
                  class="delete-btn"
                  @click="deleteAttribute(dataObj.originalKey)"
                  title="Delete attribute"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                      viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      stroke-width="2">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7
                            a2 2 0 01-2-2V6m3 0V4
                            a2 2 0 012-2h4
                            a2 2 0 012 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                </button>
              </td>
            </tr>
          </template>

          <div
            class="tooltip"
          >
            <h2>{{ isSourceAttributes ? dataObj.rawAttrName : dataObj.displayName }}</h2>
            <h4>Group: {{ getGroup(dataObj) || "None" }}</h4>
            <template v-if="getTooltipSources(dataObj.rawAttrName).length > 0">
              <hr>
              <p><strong>Sources:</strong></p>
              <ul>
                <li v-for="source in getTooltipSources(dataObj.rawAttrName)" :key="source">
                  <span
                    class="source-label"
                    :style="{ backgroundColor: getSourceColor(source) }"
                  >
                    {{ source }}
                  </span>
                </li>
              </ul>
            </template>
          </div>
        </v-tooltip>

        <tr v-if="filteredAttributes.length === 0">
          <td :colspan="isVariantAttributes ? 4 : 3" class="no-attributes">
            No attributes available. Click "Add Attribute" to create one.
          </td>
        </tr>
      </tbody>
    </table>

    <div class="attribute-flags-section" v-if="flaggedAttributesArray.length">
      <h3>Attribute Flags</h3>
      <table>
        <thead>
          <tr>
            <th>Attribute Name</th>
            <th>Predicted Value</th>
            <th>Current Attribute Values</th>
            <th>Flag Reason</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(flagObj, i) in flaggedAttributesArray"
            :key="i"
            :style="flagRowStyle(flagObj['Flag Reason'])"
          >
            <td>{{ flagObj['Attribute Name'] || '—' }}</td>
            <td>{{ flagObj['Predicted Value'] || '—' }}</td>
            <td>{{ flagObj['Current Attribute Values'] || '—' }}</td>
            <td>{{ flagObj['Flag Reason'] || '—' }}</td>
          </tr>
          <tr v-if="flaggedAttributesArray.length === 0">
            <td colspan="4" class="no-attributes-flags">
              No flagged attributes
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="showAddAttributeModal" class="modal-overlay">
      <div class="modal-content">
        <h3>Add New Attribute</h3>
        <div class="form-group">
          <label for="attribute-name">Attribute Name:</label>
           <select id="attribute-name" v-model="newAttribute.key">
            <option disabled value="">Please select one</option>
            <option v-for="tax in taxonomyOptions" :key="tax.value" :value="tax.value">
              {{ tax.text }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label for="attribute-value">Attribute Value:</label>

           <select
            v-if="isCurrentTaxonomyClosed"
            id="attribute-value-closed"
            v-model="newAttribute.value"
            :disabled="!newAttribute.key"
          >
            <option disabled value="">Please select a value</option>
            <option v-for="opt in taxonomyValueOptions" :key="opt.code" :value="opt.code">
              {{ opt.label }}
            </option>
          </select>

           <template v-else>
            <input
              type="text"
              id="attribute-value-open"
              v-model="newAttribute.value"
              placeholder="Select or enter value"
              list="attribute-value-options"
              :disabled="!newAttribute.key"
            />
            <datalist id="attribute-value-options">
              <option v-for="opt in taxonomyValueOptions" :key="opt.code" :value="opt.label"></option>
            </datalist>
          </template>
        </div>
        <div class="modal-buttons">
          <button class="cancel-btn" @click="resetNewAttributeForm">
            Cancel
          </button>
          <button
            class="add-btn"
            @click="addAttribute"
            :disabled="!canAddAttribute"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { productService, flattenTaxonomies } from '@/services/product-viewer/productService';

export default {
  name: 'ProductAttributes',
  props: {
    // ... (props are unchanged)
    attributesMatchingDefault: {
      type: Object,
      default: () => ({}),
    },
    originalAttributes: {
      type: Object,
      required: true,
    },
    editedAttributes: {
      type: Object,
      default: () => ({}),
    },
    attributesSources: {
      type: Object,
      default: () => ({}),
    },
    flaggedAttributes: {
      type: Object,
      default: () => ({}),
    },
    header: {
      type: String,
      default: 'Attributes',
    },
    selectedLanguage: {
      type: String,
      default: 'english'
    },
    sourceName: {
      type: String,
      default: '',
    },
    isVariantAttributes: {
      type: Boolean,
      default: false
    },
    selectedDb: {
      type: String,
      default: 'production',
    }
  },
  data() {
    return {
      allTaxonomies: [],
      showAddAttributeModal: false,
      newAttribute: { key: '', value: '' }, // key = taxonomy ID, value = string value

      editingAttributeKey: null,
      searchQuery: '',
      activeDropdownWrapper: null,
      dropdownStyles: {},

      hoveredRow: null,

      sourceColors: {
        Image: '#FF6F61',
        Manor: '#6B5B95',
        Vendor: '#88B04B',
        External: '#92A8D1',
        Existing: '#2c3e50',
        Unknown: '#D3D3D3',
      },
    };
  },
  watch: {
    // REVERTED: Reset the value field when the attribute name (ID) changes.
    'newAttribute.key'() {
        this.newAttribute.value = '';
    },
    selectedDb() {
        this.fetchAttributeTaxonomy();
    }
  },
  computed: {
    filteredDropdownOptions() {
      if (!this.editingAttributeKey) return [];
      const options = this.getValueOptions(this.editingAttributeKey);
      if (!this.searchQuery) return options;
      const lowerQuery = this.searchQuery.toLowerCase();
      return options.filter(opt =>
        opt.label.toLowerCase().includes(lowerQuery)
      );
    },
    isSourceAttributes() {
      return this.sourceName === "Manor Existing";
    },
    valueMapByCode() {
        const map = new Map();
        for (const taxonomy of this.allTaxonomies) {
          for (const val of taxonomy.values) {
            map.set(val.code, val);
          }
        }
        return map;
      },
      canAddAttribute() {
        return (
          this.newAttribute.key.trim() !== '' &&
          // Ensure value is not just an empty string after trim
          String(this.newAttribute.value).trim() !== ''
        );
      },
      taxonomyByIdOrCode() {
        const map = new Map();
        for (const t of this.allTaxonomies) {
          map.set(String(t.id), t);
        }
        return map;
      },
      // NEW: Checks if the selected taxonomy in the modal is a closed list
      isCurrentTaxonomyClosed() {
        if (!this.newAttribute.key) return false;
        const taxonomy = this.taxonomyByIdOrCode.get(String(this.newAttribute.key));
        // A list is closed if isClosed is explicitly true
        return taxonomy ? taxonomy.isClosed === true : false;
      },
      // REVERTED to original correct logic
      taxonomyOptions() {
        return this.allTaxonomies
          .map(t => ({
            value: t.id,
            text: t.label || t.code,
          }))
          .sort((a, b) => a.text.localeCompare(b.text));
      },
      // REVERTED to original correct logic
      taxonomyValueOptions() {
        if (!this.newAttribute.key) return [];
        const selectedTaxonomy = this.taxonomyByIdOrCode.get(String(this.newAttribute.key));
        if (!selectedTaxonomy || !selectedTaxonomy.values) return [];

        const lang = ({ english: 'en', german: 'de', french: 'fr', italian: 'it' })[this.selectedLanguage] || 'en';
        return selectedTaxonomy.values.map(v => ({
          code: v.code,
          label: v.labels[lang] || v.labels.en || v.code,
        }));
      },
      filteredAttributes() {
        const merged = { ...this.originalAttributes, ...this.editedAttributes };
        const results = [];
        for (const [key, val] of Object.entries(merged)) {
          if (val === undefined) continue;
          const detail = this.getLocalizedDisplay(key, val);
          results.push(detail);
        }
        results.sort((a, b) => {
          return this.getGroup(a).localeCompare(this.getGroup(b));
        });
        return results;
      },
      flaggedAttributesArray() {
        return Object.values(this.flaggedAttributes || {});
      },
  },
  methods: {
    // ... (no changes to startEditing, cancelEditing, selectOption, etc...) ...
    getGroup(dataObj) {
      const attribute_id = dataObj.originalKey.split("_")[1];
      return this.allTaxonomies.find(o => o.id === attribute_id)?.group || ""
    },
    getTooltipSources(rawAttrName) {
      let sources = this.attributesSources[rawAttrName];
      if (Array.isArray(sources)) {
      } else if (typeof sources === 'string') {
        sources = [sources];
      } else {
        sources = [];
      }
      return sources;
    },
    shouldHaveGroupHeader(dataObj, idx) {
      if (idx === 0) return true; // First row always needs a group

      const currentGroup = this.getGroup(dataObj);
      const previousGroup = this.getGroup(this.filteredAttributes[idx - 1]);
      return currentGroup !== previousGroup; // Since the attributes are sorted, we can add a header whenever the current group doesnt match the previous one
    },
    startEditing(dataObj, event) {
      if (this.getValueOptions(dataObj.originalKey).length > 0) {
        const rect = event.currentTarget.getBoundingClientRect();

        this.dropdownStyles = {
          position: 'absolute',
          top: `${rect.bottom + window.scrollY}px`,
          left: `${rect.left + window.scrollX}px`,
          width: `${rect.width}px`,
          minWidth: '200px'
        };

        this.activeDropdownWrapper = event.currentTarget.closest('.custom-dropdown-wrapper');
        document.addEventListener('click', this.handleClickOutside, true);

        this.editingAttributeKey = dataObj.originalKey;
        this.searchQuery = '';
        this.$nextTick(() => {
          this.$refs.dropdownInput?.[0]?.focus();
        });
      }
    },
    cancelEditing() {
      document.removeEventListener('click', this.handleClickOutside, true);
      this.editingAttributeKey = null;
      this.searchQuery = '';
      this.activeDropdownWrapper = null;
      this.dropdownStyles = {};
    },
    selectOption(dataObj, optionCode) {
      this.onDropdownChange(dataObj, optionCode);
      this.cancelEditing();
    },
    toggleMultiSelectOption(dataObj, optionCode) {
      const currentCodes = [...(dataObj.valueCodes || [])];
      const index = currentCodes.indexOf(optionCode);
      if (index > -1) {
        currentCodes.splice(index, 1);
      } else {
        currentCodes.push(optionCode);
      }
      this.onDropdownChange(dataObj, currentCodes);
    },
    handleClickOutside(event) {
      const wrapper = this.activeDropdownWrapper;
      const dropdown = this.$refs.teleportedDropdown?.[0];

      const clickedWrapper = wrapper && wrapper.contains(event.target);
      const clickedDropdown = dropdown && dropdown.contains(event.target);

      if (!clickedWrapper && !clickedDropdown) {
        this.cancelEditing();
      }
    },
    isMultiSelect(dataObj) {
        return dataObj.multipleValues && dataObj.multipleValues.length > 1;
    },
    isFlagged(key) {
        return this.flaggedAttributes[key] === true;
    },
    getValueOptions(attrKey) {
      const numericId = attrKey.includes('_') ? attrKey.split('_').pop() : attrKey;
      const attr = this.taxonomyByIdOrCode.get(String(numericId));
      if (!attr) {
        return [];
      }
      const lang = ({ english: 'en', german: 'de', french: 'fr', italian: 'it' })[this.selectedLanguage] || 'en';
      return attr.values.map(v => ({
        code: v.code,
        label: v.labels[lang] || v.labels.en || v.code,
      }));
    },
    onDropdownChange(localizedAttributeData, selectedValueIdOrIds) {
      const attributeKey = localizedAttributeData.originalKey;
      const numericAttrId = attributeKey.includes('_') ? attributeKey.split('_').pop() : attributeKey;
      const attributeDefinition = this.taxonomyByIdOrCode.get(String(numericAttrId));
      if (!attributeDefinition) {
        console.error("Could not find attribute definition for ID:", numericAttrId);
        return;
      }
      const selectedIds = Array.isArray(selectedValueIdOrIds) ? selectedValueIdOrIds : [selectedValueIdOrIds];
      const newValuesPayload = selectedIds.map(id => {
        const valueDef = attributeDefinition.values.find(v => v.code === id);
        if (!valueDef) return null;
        return {
          value: valueDef.code,
          valueLabels: {
            EN: valueDef.labels.en, DE: valueDef.labels.de,
            FR: valueDef.labels.fr, IT: valueDef.labels.it,
          }
        };
      }).filter(Boolean);

      this.$emit('attributeEdited', { key: attributeKey, value: newValuesPayload });
    },
    getLocalizedDisplay(key, valueObj) {
        if (!valueObj || typeof valueObj !== 'object') {
          return {
            originalKey: key,
            displayName: this.formatKey(key),
            displayValue: String(valueObj != null ? valueObj : ''),
            canonicalName: this.formatKey(key),
            multipleValues: [],
            valueCodes: [],
          };
        }
        const mapLang = { english: 'en', german: 'de', french: 'fr', italian: 'it' };
        const baseLang = mapLang[this.selectedLanguage] || 'en';
        function getLabel(labelsObj, lang) {
          if (!labelsObj) return null;
          const candidates = [lang.toLowerCase(), lang.toUpperCase()];
          for (const candidate of candidates) {
            if (labelsObj[candidate]) {
              return labelsObj[candidate];
            }
          }
          return null;
        }
        const rawAttrName = valueObj.attrName || key;
        let displayName = getLabel(valueObj.labels, baseLang);
        if (!displayName) {
          const taxonomy = this.taxonomyByIdOrCode.get(String(key));
          if (taxonomy) {
            displayName = taxonomy.label;
          } else {
            displayName = valueObj.attrName || key;
          }
        }
        let displayValue = '';
        let multipleValues = [];
        let valueCodes = [];
        if (Array.isArray(valueObj.attrValues) && valueObj.attrValues.length) {
          multipleValues = valueObj.attrValues.map(av => {
            const valLabel = getLabel(av.valueLabels, baseLang);
            return valLabel || (av.value != null ? String(av.value) : '');
          });
          valueCodes = valueObj.attrValues.map(av => av.value);
          displayValue = multipleValues.join(', ');
        } else if (Array.isArray(valueObj)) {
            multipleValues = valueObj.map(av => {
              const valLabel = getLabel(av.valueLabels, baseLang);
              return valLabel || (av.value != null ? String(av.value) : '');
            });
            valueCodes = valueObj.map(av => av.value);
            displayValue = multipleValues.join(', ');
        } else if (valueObj.value != null) {
          displayValue = String(valueObj.value);
          valueCodes = [valueObj.value];
        }
        return {
          originalKey: key,
          rawAttrName,
          displayName,
          displayValue,
          canonicalName: displayName,
          multipleValues,
          valueCodes
        };
      },
    onAttributeKeyEdit(oldKey, event) {
        const newKey = event.target.innerText.trim();
        if (newKey === oldKey) return;
        const merged = { ...this.originalAttributes, ...this.editedAttributes };
        const oldValue = merged[oldKey];
        this.deleteAttribute(oldKey, false);
        this.$emit('attributeEdited', { key: newKey, value: oldValue });
      },
    onAttributeValueEdit(key, event) {
        const newValue = event.target.innerText.trim();
        this.$emit('attributeEdited', { key, value: newValue });
      },
    onMultiAttributeValueEdit(key, existingArray, index, event) {
        const newStr = event.target.innerText.trim();
        const updated = [...existingArray];
        updated[index] = newStr;
        const mapLang = { english: 'en', german: 'de', french: 'fr', italian: 'it' };
        const baseLang = mapLang[this.selectedLanguage] || 'en';
        const newAttrObj = {
          attrName: key,
          attrValues: updated.map(str => ({
            value: str,
            valueLabels: { [baseLang.toUpperCase()]: str, [baseLang.toLowerCase()]: str }
          }))
        };
        this.$emit('attributeEdited', { key, value: newAttrObj });
      },
    deleteAttribute(key, emitEvent = true) {
        if (emitEvent) {
          this.$emit('attributeDeleted', key);
        }
    },
    // REVISED to handle both closed and open lists
    addAttribute() {
      if (!this.canAddAttribute) return;

      const taxonomyId = this.newAttribute.key;
      const valueInput = String(this.newAttribute.value).trim();
      const taxonomy = this.taxonomyByIdOrCode.get(String(taxonomyId));

      if (!taxonomy) {
        console.error(`Taxonomy with ID ${taxonomyId} not found.`);
        return;
      }

      const langMap = { english: 'en', german: 'de', french: 'fr', italian: 'it' };
      const lang = langMap[this.selectedLanguage] || 'en';
      const langUpper = lang.toUpperCase();

      let matchedValue;
      if (this.isCurrentTaxonomyClosed) {
        // For a closed list, the valueInput is a `code`. Find the matching value object.
        matchedValue = taxonomy.values.find(val => val.code === valueInput);
      } else {
        // For an open list, the valueInput is a `label`. Find by label.
        matchedValue = taxonomy.values.find(
            val => (val.labels[lang] || val.labels.en) === valueInput
        );
      }

      let payloadValue;
      if (matchedValue) {
        // A predefined value was selected from either list type.
        payloadValue = [{
            value: matchedValue.code,
            valueLabels: matchedValue.labels,
        }];
      } else {
        // A custom, free-text value was entered (only possible for open lists).
        payloadValue = [{
            value: valueInput,
            valueLabels: { [langUpper]: valueInput },
        }];
      }

      this.$emit('attributeEdited', { key: taxonomyId, value: payloadValue });
      this.resetNewAttributeForm();
    },
    resetNewAttributeForm() {
        this.newAttribute.key = '';
        this.newAttribute.value = '';
        this.showAddAttributeModal = false;
    },
    // ... (rest of the methods are unchanged) ...
    hoverRow(rawAttrName) {
        this.hoveredRow = rawAttrName;
    },
    leaveRow() {
        this.hoveredRow = null;
    },
    getSourceColor(source) {
        if (this.sourceColors[source]) {
          return this.sourceColors[source];
        }
        return this.sourceColors.Unknown;
    },
    getAttributeSources(rawAttrName) {
        let sources = this.attributesSources[rawAttrName];
        if (Array.isArray(sources)) {
          return sources;
        } else if (typeof sources === 'string') {
          return [sources];
        }
        return [];
    },
    formatKey(key) {
        return key
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .replace(/^\w/, c => c.toUpperCase())
          .trim();
    },
    flagRowStyle(flagReason) {
        switch (flagReason) {
          case 'Attribute not assigned to brick':
            return { backgroundColor: '#ffe0e0' };
          case 'Outside Closed List':
            return { backgroundColor: '#fff0cc' };
          case 'Other':
            return { backgroundColor: '#e0edff' };
          default:
            return { backgroundColor: '#f2f2f2' };
        }
    },
    async fetchAttributeTaxonomy() {
        const attributeTaxonomiesRes = await productService.fetchAttributeTaxonomies(this.selectedDb);
        this.allTaxonomies = flattenTaxonomies(attributeTaxonomiesRes);
        return attributeTaxonomiesRes;
    }
  },
  mounted() {
    this.fetchAttributeTaxonomy();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
  }
};
</script>
<style scoped>
.custom-dropdown-wrapper {
  position: relative;
}
.display-value {
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
  min-height: 26px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.display-value:hover {
  border-color: #dee2e6;
  background-color: #f8f9fa;
}
.multi-value-tag {
    background-color: #e9ecef;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 13px;
    white-space: nowrap;
}
.searchable-dropdown {
  z-index: 1000;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.searchable-dropdown-input {
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #dee2e6;
  font-size: 14px;
  outline: none;
}
.dropdown-options {
  max-height: 200px;
  overflow-y: auto;
}
.dropdown-option {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
}
.dropdown-option:hover {
  background-color: #f1f3f5;
}
.dropdown-option.multi {
    display: flex;
    align-items: center;
    gap: 8px;
}
.dropdown-option-none {
  padding: 8px 12px;
  color: #6c757d;
  font-style: italic;
  font-size: 14px;
}
.multi-select-checkbox {
    pointer-events: none;
}
.product-attributes {
  padding: 0;
}
.attributes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.product-attributes h3 {
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  color: #2c3e50;
}
.add-attribute-btn {
  background-color: #2c3e50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.add-attribute-btn:hover {
  background-color: #34495e;
}
.product-attributes table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border-radius: 8px;
  overflow: hidden;
}
.product-attributes th {
  background-color: #f8f9fa;
  text-align: left;
  padding: 12px 16px;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #dee2e6;
}
.product-attributes th:last-child {
  width: 60px;
  text-align: center;
}
.product-attributes td {
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}
.attribute-sources {
  width: 120px;
  word-break: break-word;
}
.sources-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.source-badge {
  display: inline-block;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.no-source {
  color: #6c757d;
  font-style: italic;
}
.attribute-key {
  font-weight: 500;
  width: 40%;
  word-break: break-word;
}
.attribute-value {
  word-break: break-word;
}
.attribute-key span,
.attribute-value span {
  display: inline-block;
  min-width: 50px;
  padding: 4px;
  border-radius: 4px;
  border: 1px solid transparent;
  transition: all 0.2s;
}
.attribute-key span:hover,
.attribute-value span:hover,
.attribute-key span:focus,
.attribute-value span:focus {
  border-color: #dee2e6;
  background-color: #f8f9fa;
  outline: none;
}
.attribute-actions {
  text-align: center;
  white-space: nowrap;
}
.delete-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background-color 0.2s;
}
.delete-btn:hover {
  background-color: #f8d7da;
}
.no-attributes {
  text-align: center;
  color: #6c757d;
  font-style: italic;
  padding: 20px 0;
}
.flagged td {
  background-color: #fff3cd;
}
.product-attributes tbody tr:hover {
  background-color: #f5f5f5;
}
.highlighted td {
  background-color: #eaf2f8 !important;
}
.tooltip p {
  margin: 0 0 5px 0;
}
.tooltip ul {
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
}
.tooltip ul li {
  margin-bottom: 5px;
}
.source-label {
  display: inline-block;
  color: #fff;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 14px;
}
.attribute-flags-section {
  margin-top: 40px;
}
.attribute-flags-section h3 {
  margin: 0 0 16px;
  font-size: 20px;
  color: #c0392b;
  border-bottom: 2px solid #ccc;
  padding-bottom: 6px;
}
.attribute-flags-section table {
  margin-bottom: 0;
}
.no-attributes-flags {
  text-align: center;
  font-style: italic;
  color: #666;
}
.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0,0,0,0.5);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
}
.modal-content {
  background-color: #fff;
  border-radius: 8px;
  padding: 24px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}
.modal-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #2c3e50;
}
.form-group {
  margin-bottom: 16px;
}
.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #2c3e50;
}
.form-group input, .form-group select { /* UPDATED to include select */
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 16px;
  background-color: #fff; /* Ensure select has a background */
}
.form-group input:focus, .form-group select:focus { /* UPDATED to include select */
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
}
.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}
.modal-buttons button {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}
.cancel-btn {
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  color: #212529;
}
.add-btn {
  background-color: #2c3e50;
  border: 1px solid #2c3e50;
  color: #fff;
}
.add-btn:hover:not(:disabled) {
  background-color: #34495e;
}
.add-btn:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.65;
}
.multi-attr-values {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.multi-value-item {
  background-color: #f0f4f8;
  padding: 4px 8px;
  border-radius: 4px;
}
.multi-value-item span {
  outline: none;
}
</style>